import apiService, { ApiError } from './apiService';

/**
 * Marketer Service
 * Handles all marketer-related API operations
 */
class MarketerService {
  /**
   * Format date to API required format (YYYY-MM-DD HH:mm:ss)
   * @param {string|Date} date - Date to format
   * @param {boolean} isEndDate - Whether this is end date (adds 23:59:59)
   * @returns {string} Formatted date string
   */
  formatDateForAPI(date, isEndDate = false) {
    if (!date) return null;

    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return null;

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');

    const time = isEndDate ? '23:59:59' : '00:00:00';

    return `${year}-${month}-${day} ${time}`;
  }

  /**
   * Get list of marketers with pagination
   * @param {Object} params - Pagination parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 50)
   * @param {string} params.keyword - Search keyword (optional)
   * @param {string} params.dateFrom - Start date for filtering (optional)
   * @param {string} params.dateTo - End date for filtering (optional)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Marketer list response
   */
  async getMarketerList(params = {}, options = {}) {
    const { page = 1, size = 50, keyword, dateFrom, dateTo, order } = params;

    try {
      // Build request data
      const requestData = {
        page,
        size,
        order,
      };

      // Add keyword to payload if provided
      if (keyword && keyword.trim()) {
        requestData.keyword = keyword.trim();
      }

      // Add dateFilter to payload if dates are provided
      if (dateFrom && dateTo) {
        const startDate = this.formatDateForAPI(dateFrom, false);
        const endDate = this.formatDateForAPI(dateTo, true);

        if (startDate && endDate) {
          requestData.dateFilter = {
            start: startDate,
            end: endDate,
          };
        }
      }

      const response = await apiService.skaiNetRequest('/api/v1/account/list', {
        method: 'POST',
        data: requestData,
        ...options,
      });

      // Validate response structure
      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from marketer list API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to fetch marketer list:', error);

      // Re-throw with additional context for marketer-specific errors
      if (error instanceof ApiError) {
        throw error;
      }

      // Handle unexpected errors
      throw new ApiError(
        'Failed to retrieve marketer list',
        0,
        'MARKETER_LIST_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Get marketer details by ID
   * @param {string} marketerId - Marketer ID
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Marketer details response
   */
  async getMarketerDetails(marketerId, options = {}) {
    if (!marketerId) {
      throw new ApiError('Marketer ID is required', 400, 'MISSING_MARKETER_ID');
    }

    try {
      // Note: Endpoint needs to be confirmed with backend team
      const response = await apiService.skaiNetRequest(
        `/api/v1/account/${marketerId}`,
        {
          method: 'GET',
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error(`Failed to fetch marketer details for ID ${marketerId}:`, error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to retrieve marketer details',
        0,
        'MARKETER_DETAILS_ERROR',
        { marketerId, originalError: error.message }
      );
    }
  }

  /**
   * Search marketers by criteria
   * @param {Object} searchParams - Search parameters
   * @param {string} searchParams.query - Search query (name, email, etc.)
   * @param {number} searchParams.page - Page number (default: 1)
   * @param {number} searchParams.size - Page size (default: 50)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Search results response
   */
  async searchMarketers(searchParams = {}, options = {}) {
    const { query, page = 1, size = 50 } = searchParams;

    try {
      // Note: Endpoint needs to be confirmed with backend team
      const response = await apiService.skaiNetRequest(
        '/api/v1/account/search',
        {
          method: 'POST',
          data: {
            query,
            page,
            size,
          },
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to search marketers:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to search marketers',
        0,
        'MARKETER_SEARCH_ERROR',
        { searchParams, originalError: error.message }
      );
    }
  }

  /**
   * Get marketer statistics and metrics
   * @param {string} marketerId - Marketer ID (optional, gets current user stats if not provided)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Marketer statistics response
   */
  async getMarketerStats(marketerId = null, options = {}) {
    try {
      const endpoint = marketerId
        ? `/api/v1/account/${marketerId}/stats`
        : '/api/v1/account/stats';

      const response = await apiService.skaiNetRequest(endpoint, {
        method: 'GET',
        ...options,
      });

      return response;
    } catch (error) {
      console.error('Failed to fetch marketer statistics:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to retrieve marketer statistics',
        0,
        'MARKETER_STATS_ERROR',
        { marketerId, originalError: error.message }
      );
    }
  }

  /**
   * Transform API marketer data to frontend format
   * @param {Object} apiMarketer - Raw marketer data from API
   * @returns {Object} Transformed marketer data
   */
  transformMarketerData(apiMarketer) {
    if (!apiMarketer) return null;

    return {
      id: apiMarketer.id,
      accountId: apiMarketer.accountId,
      firstName: apiMarketer.name?.split(' ')[0] || 'N/A',
      lastName: apiMarketer.name?.split(' ').slice(1).join(' ') || 'N/A',
      fullName: apiMarketer.name || 'N/A',
      email: apiMarketer.email || 'N/A',
      phone: apiMarketer.phone || 'N/A',
      status:
        apiMarketer.paidExpired &&
          new Date(apiMarketer.paidExpired) > new Date()
          ? 'Plus'
          : 'Free',
      joinDate: apiMarketer.createdAt
        ? new Date(apiMarketer.createdAt).toLocaleDateString('en-US')
        : 'N/A',
      referralCode: apiMarketer.referral || 'N/A',
      upline: apiMarketer.upline || 'N/A',
      downline: apiMarketer.downline || 0,
      partner: apiMarketer.partner || 0,
      careerPoint: Math.round(apiMarketer.careerPoint || 0),
      careerLevel: apiMarketer.careerLevel || 0,
      careerBonus: apiMarketer.careerBonus || 0,
      careerCommission: apiMarketer.careerCommision || 0, // Note: API has typo "careerCommision"
      dateOfBirth: apiMarketer.dateOfBirth,
      paidExpired: apiMarketer.paidExpired,
      quotas: apiMarketer.quotas || {
        shopper: { used: 0, total: 0 },
        marketer: { used: 0, total: 0 },
        partner: { used: 0, total: 0 },
      },
      // Add computed fields
      isActive: apiMarketer.status === 'A' ? 'Active' : 'Delete',
      hasDownline: (apiMarketer.downline || 0) > 0,
      createdAt: apiMarketer.createdAt,
    };
  }

  /**
   * Get formatted marketer list with transformed data
   * @param {Object} params - Pagination parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 50)
   * @param {string} params.keyword - Search keyword (optional)
   * @param {string} params.dateFrom - Start date for filtering (optional)
   * @param {string} params.dateTo - End date for filtering (optional)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Formatted marketer list response
   */
  async getFormattedMarketerList(params = {}, options = {}) {
    try {
      const response = await this.getMarketerList(params, options);

      // Transform the marketer data
      const transformedData = {
        ...response.data,
        data: response.data.data.map(marketer =>
          this.transformMarketerData(marketer)
        ),
      };

      return {
        ...response,
        data: transformedData,
      };
    } catch (error) {
      console.error('Failed to get formatted marketer list:', error);
      throw error;
    }
  }

  /**
   * Get formatted marketer details by ID
   * @param {string} marketerId - Marketer ID
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Formatted marketer details
   */
  async getFormattedMarketerDetails(marketerId, options = {}) {
    try {
      const response = await this.getMarketerDetails(marketerId, options);

      // Transform the marketer data
      const transformedData = this.transformMarketerData(response.data);

      return {
        ...response,
        data: transformedData,
      };
    } catch (error) {
      console.error(
        `Failed to get formatted marketer details for ID ${marketerId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Create a new marketer
   * @param {Object} marketerData - Marketer data to create
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Created marketer response
   */
  async createMarketer(marketerData, options = {}) {
    try {
      const response = await apiService.skaiNetRequest('/api/v1/account', {
        method: 'POST',
        data: marketerData,
        ...options,
      });

      return response;
    } catch (error) {
      console.error('Failed to create marketer:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to create marketer',
        0,
        'MARKETER_CREATE_ERROR',
        { marketerData, originalError: error.message }
      );
    }
  }

  /**
   * Update marketer information
   * @param {string} marketerId - Marketer ID
   * @param {Object} updateData - Data to update
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Updated marketer response
   */
  async updateMarketer(marketerId, updateData, options = {}) {
    if (!marketerId) {
      throw new ApiError('Marketer ID is required', 400, 'MISSING_MARKETER_ID');
    }

    try {
      const response = await apiService.skaiNetRequest(
        `/api/v1/account/${marketerId}`,
        {
          method: 'PUT',
          data: updateData,
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error(`Failed to update marketer ${marketerId}:`, error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to update marketer',
        0,
        'MARKETER_UPDATE_ERROR',
        { marketerId, updateData, originalError: error.message }
      );
    }
  }

  /**
   * Delete/deactivate a marketer
   * @param {string} marketerId - Marketer ID
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Delete response
   */
  async deleteMarketer(marketerId, options = {}) {
    if (!marketerId) {
      throw new ApiError('Marketer ID is required', 400, 'MISSING_MARKETER_ID');
    }

    try {
      const response = await apiService.skaiNetRequest(
        `/api/v1/account/${marketerId}`,
        {
          method: 'DELETE',
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error(`Failed to delete marketer ${marketerId}:`, error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to delete marketer',
        0,
        'MARKETER_DELETE_ERROR',
        { marketerId, originalError: error.message }
      );
    }
  }

  /**
   * Get marketer downline list
   * @param {string} referrerAccountId - Account ID of the marketer whose downline we want to fetch
   * @param {Object} params - Pagination parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 50)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Downline list response
   */
  async getMarketerDownline(referrerAccountId, params = {}, options = {}) {
    if (!referrerAccountId) {
      throw new ApiError(
        'Referrer Account ID is required',
        400,
        'MISSING_REFERRER_ACCOUNT_ID'
      );
    }

    const { page = 1, size = 50, order, ...additionalFilters } = params;

    try {
      const requestData = {
        page,
        size,
        accountId: referrerAccountId,
        order,
        from: 'M',
        ...additionalFilters,
      };

      const response = await apiService.skaiNetRequest(
        '/api/v1/account/network',
        {
          method: 'POST',
          data: requestData,
          ...options,
        }
      );

      // Validate response structure
      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from downline list API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error(`Failed to fetch downline for account ${referrerAccountId}:`, error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to retrieve marketer downline',
        0,
        'MARKETER_DOWNLINE_ERROR',
        { referrerAccountId, originalError: error.message }
      );
    }
  }

  /**
   * Get shopper downline list
   */
  async getShopperDownline(referrerAccountId, params = {}, options = {}) {
    if (!referrerAccountId) {
      throw new ApiError(
        'Referrer Account ID is required',
        400,
        'MISSING_REFERRER_ACCOUNT_ID'
      );
    }

    const { page = 1, size = 50 } = params;

    try {
      const requestData = {
        page,
        size,
        accountId: referrerAccountId,
        from: 'S',
      };

      const response = await apiService.skaiNetRequest(
        '/api/v1/account/shoppers',
        {
          method: 'POST',
          data: requestData,
          ...options,
        }
      );

      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from shopper downline API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error(
        `Failed to fetch shopper downline for account ${referrerAccountId}:`,
        error
      );
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to retrieve shopper downline',
        0,
        'SHOPPER_DOWNLINE_ERROR',
        { referrerAccountId, originalError: error.message }
      );
    }
  }

  /**
   * Get partner downline list
   */
  async getPartnerDownline(referrerAccountId, params = {}, options = {}) {
    if (!referrerAccountId) {
      throw new ApiError(
        'Referrer Account ID is required',
        400,
        'MISSING_REFERRER_ACCOUNT_ID'
      );
    }

    const { page = 1, size = 50 } = params;

    try {
      const requestData = {
        page,
        size,
        accountId: referrerAccountId,
        from: 'P',
      };

      const response = await apiService.skaiNetRequest(
        '/api/v1/account/shoppers',
        {
          method: 'POST',
          data: requestData,
          ...options,
        }
      );

      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from partner downline API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error(
        `Failed to fetch partner downline for account ${referrerAccountId}:`,
        error
      );
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to retrieve partner downline',
        0,
        'PARTNER_DOWNLINE_ERROR',
        { referrerAccountId, originalError: error.message }
      );
    }
  }

  /**
   * Get formatted shopper downline list
   */
  async getFormattedShopperDownline(
    referrerAccountId,
    params = {},
    options = {}
  ) {
    try {
      const response = await this.getShopperDownline(
        referrerAccountId,
        params,
        options
      );
      const transformedData = {
        ...response.data,
        data: response.data.data.map(marketer =>
          this.transformMarketerData(marketer)
        ),
      };
      return { ...response, data: transformedData };
    } catch (error) {
      console.error('Failed to get formatted shopper downline:', error);
      throw error;
    }
  }

  /**
   * Get formatted partner downline list
   */
  async getFormattedPartnerDownline(
    referrerAccountId,
    params = {},
    options = {}
  ) {
    try {
      const response = await this.getPartnerDownline(
        referrerAccountId,
        params,
        options
      );
      const transformedData = {
        ...response.data,
        data: response.data.data.map(marketer =>
          this.transformMarketerData(marketer)
        ),
      };
      return { ...response, data: transformedData };
    } catch (error) {
      console.error('Failed to get formatted partner downline:', error);
      throw error;
    }
  }

  /**
   * Get formatted marketer downline with transformed data
   * @param {string} referrerAccountId - Account ID of the marketer whose downline we want to fetch
   * @param {Object} params - Pagination parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 50)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Formatted downline list response
   */
  async getFormattedMarketerDownline(
    referrerAccountId,
    params = {},
    options = {}
  ) {
    try {
      const response = await this.getMarketerDownline(referrerAccountId, params, options);

      // Transform the downline data
      const transformedData = {
        ...response.data,
        data: response.data.data.map(marketer =>
          this.transformMarketerData(marketer)
        ),
      };

      return {
        ...response,
        data: transformedData,
      };
    } catch (error) {
      console.error('Failed to get formatted marketer downline:', error);
      throw error;
    }
  }

  /**
   * Export marketer list - now returns JSON data instead of CSV
   * @param {Object} params - Filtering parameters
   * @param {string} params.keyword - Search keyword (optional)
   * @param {string} params.dateFrom - Start date for filtering (optional)
   * @param {string} params.dateTo - End date for filtering (optional)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Export information as JSON
   */
  async exportMarketerList(params = {}, options = {}) {
    const { keyword, dateFrom, dateTo, order } = params;

    try {
      const requestData = { order };

      if (keyword && keyword.trim()) {
        requestData.keyword = keyword.trim();
      }

      if (dateFrom && dateTo) {
        const startDate = this.formatDateForAPI(dateFrom, false);
        const endDate = this.formatDateForAPI(dateTo, true);

        if (startDate && endDate) {
          requestData.dateFilter = {
            start: startDate,
            end: endDate,
          };
        }
      }

      const response = await apiService.skaiNetRequest('/api/v1/account/list-export', {
        method: 'POST',
        data: requestData,
        ...options,
      });

      // Backend now returns JSON instead of CSV
      if (!response.data || typeof response.data !== 'object') {
        throw new ApiError(
          'Invalid response format from marketer export API. Expected JSON object.',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to export marketer list:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to export marketer list',
        0,
        'MARKETER_EXPORT_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Get marketer downline/upline tree
   * @param {string} marketerId - Marketer ID
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Tree structure response
   */
  async getMarketerTree(marketerId, options = {}) {
    if (!marketerId) {
      throw new ApiError('Marketer ID is required', 400, 'MISSING_MARKETER_ID');
    }

    try {
      const response = await apiService.skaiNetRequest(
        `/api/v1/account/${marketerId}/tree`,
        {
          method: 'GET',
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error(`Failed to fetch marketer tree for ${marketerId}:`, error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to retrieve marketer tree',
        0,
        'MARKETER_TREE_ERROR',
        { marketerId, originalError: error.message }
      );
    }
  }
}

// Create singleton instance
const marketerService = new MarketerService();

export default marketerService;
export { MarketerService };
